import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Brain, Sparkles, Settings, Share, Menu, Sun, Moon, X, Loader2 } from "lucide-react";
import { LanguageToggle } from "./LanguageToggle";
import { ShareDropdown } from "./ShareDropdown";
import { type Language } from "../i18n";
import { type Analysis } from "../types";
import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

interface HeaderV3Props {
  language: Language;
  onLanguageChange: (language: Language) => void;
  theme: 'dark' | 'light';
  onThemeChange: (theme: 'dark' | 'light') => void;
  analysisTitle?: string;
  onGoHome?: () => void;
  onNewAnalysis?: () => void;
  // Новые пропсы для шеринга
  analysis?: Analysis;
  onShare?: (analysis?: Analysis) => Promise<string | null>;
  isSharing?: boolean;
  shareUrl?: string | null;
  hasBeenShared?: boolean;
  onExportPDF?: () => Promise<void>;
  isExportingPDF?: boolean;
  shareError?: string | null;
  // Пропсы для работы с флагом public
  shareId?: string;
  onPublicToggle?: (isPublic: boolean) => void;
}

export function HeaderV3({ 
  language, 
  onLanguageChange, 
  theme, 
  onThemeChange, 
  analysisTitle,
  onGoHome,
  onNewAnalysis,
  // Новые пропсы для шеринга
  analysis,
  onShare,
  isSharing = false,
  shareUrl,
  hasBeenShared = false,
  onExportPDF,
  isExportingPDF = false,
  shareError,
  // Пропсы для работы с флагом public
  shareId,
  onPublicToggle
}: HeaderV3Props) {
  const { t } = useTranslation('common');
  const isDark = theme === 'dark';
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isShareDropdownOpen, setIsShareDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const shareButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 20);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Обработчик нажатия кнопки "Поделиться"
  const handleShareClick = async () => {
    if (isSharing) return; // Защита от повторных нажатий
    
    // Если dropdown уже открыт, просто закрываем его
    if (isShareDropdownOpen) {
      setIsShareDropdownOpen(false);
      return;
    }
    
    // Если уже был шеринг и нет изменений, просто открываем dropdown
    if (hasBeenShared && analysis && !analysis.isModified) {
      setIsShareDropdownOpen(true);
      return;
    }
    
    // Если есть функция onShare, вызываем её для создания шеринга
    if (onShare) {
      try {
        await onShare(analysis);
        // После успешного шеринга открываем dropdown
        setIsShareDropdownOpen(true);
      } catch (error) {
        console.error('Ошибка при создании шеринга:', error);
        // В случае ошибки не открываем dropdown
      }
    } else {
      // Если нет функции onShare, просто открываем dropdown
      setIsShareDropdownOpen(true);
    }
  };
  
  return (
    <>
      <header className={`sticky top-0 z-50 transition-all duration-500 ease-out ${
        isScrolled 
          ? isDark
            ? 'bg-slate-900/80 backdrop-blur-xl shadow-lg border-b border-white/20'
            : 'bg-white/80 backdrop-blur-xl shadow-lg border-b border-gray-200/40'
          : 'bg-transparent shadow-none border-b border-transparent'
      }`}>
        <div className="container mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-12 xl:px-16 py-2 sm:py-3">
          <div className="flex items-center justify-between">
            {/* Компактный логотип */}
            <a 
              href={`/${language}/`}
              className="flex items-center flex-shrink-0 cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (onGoHome && typeof onGoHome === 'function') {
                  onGoHome();
                }
              }}
            >
              <img 
                src={isDark ? "/images/Plinus_FullLogo_W_H_52x160mm@4x.png" : "/images/Plinus_Valonik_Logo_Vector_2025.svg"}
                alt={language === 'ru' ? 'ПЛИНУС - ИИ анализ решений' : 'PLINUS - AI Decision Analysis'} 
                className="h-10 sm:h-12 lg:h-14 object-contain transition-all duration-300 pointer-events-none"
              />
              <Badge className={`hidden lg:block ml-2 pointer-events-none ${
                isDark 
                  ? 'bg-gradient-to-r from-purple-500/20 to-blue-500/20 text-purple-300 border-purple-500/30'
                  : 'bg-gradient-to-r from-purple-500/10 to-blue-500/10 text-purple-700 border-purple-300'
              }`}>
                AI Powered
              </Badge>
            </a>
            
            {/* Desktop menu */}
            <div className="hidden lg:flex items-center gap-3">
              {/* Кнопка "Поделиться" - показываем только если onShare передан */}
              {onShare && (
                <Button 
                  ref={shareButtonRef}
                  variant="default"
                  size="sm" 
                  onClick={handleShareClick}
                  disabled={isSharing}
                  data-metrika="share"
                  className={`relative group ${
                    isDark 
                      ? 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white' 
                      : 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white'
                  } ${isShareDropdownOpen ? 'ring-2 ring-purple-400/50' : ''} font-medium transition-all duration-200 px-3 py-2 border-0 rounded-lg ${
                    isSharing ? 'opacity-75 cursor-not-allowed' : ''
                  }`}
                >
                  {isSharing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Share className="w-4 h-4 mr-2" />
                  )}
                  {isSharing ? t('creating') : t('share')}
                </Button>
              )}
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onThemeChange(isDark ? 'light' : 'dark')}
                className={isDark 
                  ? 'text-slate-300 hover:text-white hover:bg-white/10' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }
              >
                {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </Button>
              
              
              <LanguageToggle 
                currentLanguage={language}
                onLanguageChange={onLanguageChange}
                variant={isDark ? "dark" : "light"}
              />
            </div>

            {/* Mobile menu button */}
            <div className="flex items-center gap-2 lg:hidden">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onThemeChange(isDark ? 'light' : 'dark')}
                className={isDark 
                  ? 'text-slate-300 hover:text-white hover:bg-white/10' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }
              >
                {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </Button>
              
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={isDark 
                  ? 'text-slate-300 hover:text-white hover:bg-white/10' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }
              >
                {isMobileMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile menu overlay */}
      {isMobileMenuOpen && (
        <div className={`lg:hidden fixed inset-0 top-[73px] z-40 ${
          isDark ? 'bg-slate-900/95' : 'bg-white/95'
        } backdrop-blur-xl border-t ${
          isDark ? 'border-white/10' : 'border-gray-200/60'
        }`}>
          <div className="container mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-12 xl:px-16 py-6 space-y-4">
            <div className="flex items-center justify-between pb-4 border-b border-gray-200/20">
              <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('menu')}
              </h2>
              <Badge className={`${
                isDark 
                  ? 'bg-gradient-to-r from-purple-500/20 to-blue-500/20 text-purple-300 border-purple-500/30'
                  : 'bg-gradient-to-r from-purple-500/10 to-blue-500/10 text-purple-700 border-purple-300'
              }`}>
                AI Powered
              </Badge>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between py-3">
                <span className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                  {t('languageToggle')}
                </span>
                <LanguageToggle 
                  currentLanguage={language}
                  onLanguageChange={onLanguageChange}
                  variant={isDark ? "dark" : "light"}
                />
              </div>
              
              
              <Button 
                variant="ghost" 
                size="lg"
                className={`w-full justify-start ${isDark 
                  ? 'text-slate-300 hover:text-white hover:bg-white/10' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Settings className="w-5 h-5 mr-3" />
                {t('settings')}
              </Button>
            </div>
          </div>
        </div>
      )}

      <ShareDropdown
        isOpen={isShareDropdownOpen}
        onClose={() => setIsShareDropdownOpen(false)}
        language={language}
        theme={theme}
        analysisTitle={analysisTitle || ""}
        triggerRef={shareButtonRef}
        shareUrl={shareUrl}
        hasBeenShared={hasBeenShared}
        onExportPDF={onExportPDF}
        isExportingPDF={isExportingPDF}
        shareError={shareError}
        shareId={shareId}
        externalId={analysis?.external_id}
        onPublicToggle={onPublicToggle}
      />
    </>
  );
}
